<template>
  <div class="wizard-root">
    <!-- Premium Step Header -->
    <div v-if="step !== 3" class="wizard-header">
      <div class="wizard-steps">
        <div
          v-for="(s, i) in stepConfig"
          :key="i"
          class="wizard-step"
          :class="{ active: step === i + 1, completed: step > i + 1, clickable: step > i + 1 }"
          @click="step > i + 1 && (step = i + 1)">
          <div class="step-circle">
            <v-icon v-if="step > i + 1" x-small color="white">mdi-check</v-icon>
            <span v-else>{{ i + 1 }}</span>
          </div>
          <div class="step-label">{{ s.label }}</div>
          <div v-if="i < stepConfig.length - 1" class="step-line" :class="{ filled: step > i + 1 }"></div>
        </div>
      </div>
    </div>

    <!-- Step Content -->
    <div class="wizard-body" :class="{ 'wizard-body-fullheight': step === 3 || step === 5 || (step === 4 && planningMode === 'assistant') }">
      <!-- Step 1: Wochen-Vorlage -->
      <div v-if="step === 1" class="wizard-step-content">
        <div v-if="availableStudios.length > 1" class="mb-4" style="max-width: 320px">
          <v-select
            filled
            dense
            dark
            hide-details
            :items="availableStudios"
            label="Studio wählen"
            v-model="selectedStudio"
            item-text="name"
            item-value="_id"
            @change="handleStudioChange"
            class="wizard-select"></v-select>
        </div>
        <div v-else-if="availableStudios.length === 1 && !selectedStudio">{{ autoSelectStudio() }}</div>

        <weekly-template-planner
          v-if="!loading && selectedStudio && settings.shifts && settings.shifts.length > 0"
          :studio-id="selectedStudio"
          :available-shifts="settings.shifts"
          :key="`weekly-planner-${selectedStudio}`"
          ref="weeklyPlanner"
          @template-saved="handleTemplateUpdate" />
        <div v-else-if="loading" class="wizard-loading">
          <v-progress-circular indeterminate color="green accent-3" size="56" width="3"></v-progress-circular>
          <p class="mt-4" style="color: rgba(255, 255, 255, 0.4)">Lade Daten…</p>
        </div>
        <div v-else class="wizard-loading">
          <v-icon size="48" color="rgba(255,255,255,0.15)">mdi-alert-circle-outline</v-icon>
          <p class="mt-3" style="color: rgba(255, 255, 255, 0.4)">
            {{ !selectedStudio ? "Bitte Studio auswählen." : "Keine Schichtvorlagen gefunden." }}
          </p>
        </div>
      </div>

      <!-- Step 2: Spezialtage & Monat -->
      <div v-if="step === 2" class="wizard-step-content">
        <div class="d-flex align-center mb-5" style="gap: 16px">
          <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn depressed dark v-bind="attrs" v-on="on" class="wizard-month-btn">
                <v-icon left small>mdi-calendar-month</v-icon>{{ monthDisplayText }}
                <v-icon right small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-date-picker v-model="planningMonth" type="month" no-title scrollable dark @click:month="onMonthChanged"></v-date-picker>
          </v-menu>
          <span class="text-caption" style="color: rgba(255, 255, 255, 0.35)">
            {{ $moment(planningDateRange[0]).format("DD.MM") }} – {{ $moment(planningDateRange[1]).format("DD.MM.YYYY") }}
          </span>
        </div>
        <div v-if="existingPlanCheckLoading" class="wizard-status-card">
          <v-progress-circular indeterminate color="green accent-3" size="38" width="3"></v-progress-circular>
          <div>
            <div class="text-subtitle-2 font-weight-bold white--text">Monat wird geprüft</div>
            <div class="text-caption wizard-status-copy">Wir stellen sicher, dass noch kein Einsatzplan besteht.</div>
          </div>
        </div>

        <div v-else-if="existingPlanCheckError" class="wizard-status-card wizard-status-card-error">
          <div class="wizard-status-icon error-icon"><v-icon color="red lighten-2">mdi-shield-alert-outline</v-icon></div>
          <div class="flex-grow-1">
            <div class="text-subtitle-1 font-weight-bold white--text">Monatsstatus konnte nicht geprüft werden</div>
            <div class="text-body-2 wizard-status-copy mt-1">
              Zum Schutz bestehender Pläne bleibt die weitere Planung gesperrt. Bitte prüfe den Monat erneut.
            </div>
            <v-btn class="mt-4 text-none" outlined color="red lighten-2" :loading="existingPlanCheckLoading" @click="ensureExistingPlansLoaded(true)">
              <v-icon left small>mdi-refresh</v-icon>Erneut prüfen
            </v-btn>
          </div>
        </div>

        <div v-else-if="selectedExistingPlan" class="wizard-status-card wizard-status-card-existing">
          <div class="wizard-status-icon"><v-icon color="amber lighten-1">mdi-calendar-lock</v-icon></div>
          <div class="flex-grow-1">
            <div class="text-h6 font-weight-bold white--text">{{ monthDisplayText }} ist bereits geplant</div>
            <div class="text-body-2 wizard-status-copy mt-1">
              Bestehende Monate bearbeitest du direkt in der Monatsansicht. So werden vorhandene Schichten und Zuweisungen nicht versehentlich über
              &bdquo;Neuen Monat planen&ldquo; verändert.
            </div>
            <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px">
              <v-chip small outlined color="amber lighten-1">
                <v-icon left x-small>mdi-calendar-check</v-icon>{{ selectedExistingPlan.totalShifts || 0 }} Schichten
              </v-chip>
              <v-chip small outlined color="green accent-3">{{ selectedExistingPlan.assignedShifts || 0 }} besetzt</v-chip>
              <v-chip v-if="selectedExistingPlan.unassignedShifts" small outlined color="orange">
                {{ selectedExistingPlan.unassignedShifts }} offen
              </v-chip>
            </div>
            <div class="d-flex align-center flex-wrap mt-5" style="gap: 10px">
              <v-btn depressed color="green accent-3" class="black--text font-weight-bold text-none" @click="openExistingMonth">
                <v-icon left small>mdi-open-in-new</v-icon>Bestehenden Monat öffnen
              </v-btn>
              <v-btn text color="white" class="text-none" @click="dateMenu = true">
                <v-icon left small>mdi-calendar-month</v-icon>Anderen Monat wählen
              </v-btn>
            </div>
          </div>
        </div>

        <ShiftManagementMainDialogCreateSpecialDays
          v-else-if="selectedStudio && hasExistingPlanOverview"
          :studio-id="selectedStudio"
          :planning-start-date="planningDateRange[0]"
          :planning-end-date="planningDateRange[1]"
          :available-shifts="settings.shifts"
          :employees="employeesBasic"
          :weekly-template-data="loadedWeeklyTemplate"
          :public-holidays="publicHolidays"
          @back="step = 1"
          @next="handleSpecialDaysNext" />
      </div>

      <!-- Step 3: Mitarbeiter -->
      <div v-if="step === 3" class="wizard-step-content wizard-step-fullheight">
        <shift-management-employee-settings
          v-if="selectedStudio && planningDateRange.length === 2"
          :studio-id="selectedStudio"
          :planning-start-date="planningDateRange[0]"
          :planning-end-date="planningDateRange[1]"
          :weekly-template-data="loadedWeeklyTemplate"
          :available-shifts="settings.shifts"
          :school-holidays="settings.schoolHolidays || []"
          :confirmed-employee-settings="employeesBasic"
          ref="employeeSettings"
          @proceed-state="employeeSettingsCanProceed = $event"
          @next="nextStep" />
        <div v-else class="wizard-loading">
          <p style="color: rgba(255, 255, 255, 0.4)">Bitte zuerst Studio und Zeitraum wählen.</p>
          <v-btn text color="green accent-3" @click="step = 1" class="mt-3">Zurück</v-btn>
        </div>
      </div>

      <!-- Step 4: Zuweisung -->
      <div v-if="step === 4" class="wizard-step-content wizard-step-fullheight">
        <div v-if="workHoursSummaryWarning && !planningMode" class="wizard-loading fill-height" style="min-height: 400px">
          <v-icon size="54" color="amber">mdi-calculator-variant-outline</v-icon>
          <div class="text-h6 font-weight-bold white--text mt-4">Sollstunden noch nicht vollständig</div>
          <p class="mt-2 mb-4 text-center" style="max-width: 620px; color: rgba(255, 255, 255, 0.62)">
            {{ workHoursSummaryWarning }} Der Einsatzplan bleibt erhalten; der Assistent startet erst mit verlässlichen Werten.
          </p>
          <div class="d-flex align-center" style="gap: 10px">
            <v-btn text color="white" class="text-none" @click="step = 3">Zurück zu den Einstellungen</v-btn>
            <v-btn
              depressed
              color="amber"
              class="black--text text-none font-weight-bold"
              :loading="workHoursSummaryRetrying"
              @click="retryAssistantSummary">
              <v-icon left small>mdi-refresh</v-icon>Erneut berechnen
            </v-btn>
          </div>
        </div>
        <div
          v-else-if="assigning && planningMode !== 'assistant'"
          class="wizard-loading fill-height"
          style="min-height: 400px; display: flex; flex-direction: column; justify-content: center; align-items: center">
          <v-progress-circular indeterminate color="green accent-3" size="56" width="3"></v-progress-circular>
          <p class="mt-4" style="color: rgba(255, 255, 255, 0.4)">Bereite Zuweisungs-Assistenten vor…</p>
        </div>
        <shift-management-assistant
          v-else
          :studio-id="selectedStudio"
          :start-date="planningDateRange[0]"
          :end-date="planningDateRange[1]"
          :available-shifts="settings.shifts"
          :school-holidays="settings.schoolHolidays || []"
          :public-holidays="publicHolidays"
          :employees="confirmedEmployeeSettings && confirmedEmployeeSettings.length > 0 ? confirmedEmployeeSettings : employeesBasic"
          :previous-context="previousContext"
          @back="prevStep"
          @view-plan="handleAssistantFinish" />
      </div>

      <!-- Step 5: Einsatzplan -->
      <div v-if="step === 5" class="wizard-step-content wizard-step-fullheight">
        <shift-management-roster-view
          v-if="assignedPlanData.length > 0 || unassignedShifts.length > 0"
          :plan-data="assignedPlanData"
          :studioId="selectedStudio"
          :availableShifts="settings.shifts"
          :unassignedShifts="unassignedShifts"
          :employees="assignedEmployees"
          :available-employees="availableEmployeesToAdd"
          :start-date="planningDateRange[0]"
          :school-holidays="settings.schoolHolidays || []"
          :public-holidays="publicHolidays"
          :previous-context="previousContext"
          :loading="assigning"
          @add-employee="onAddEmployee"
          @refresh="refreshData" />
        <div v-else class="wizard-loading">
          <v-icon size="48" color="rgba(255,255,255,0.15)">mdi-calendar-question</v-icon>
          <p class="mt-3" style="color: rgba(255, 255, 255, 0.4)">
            {{ assigning ? "Plan wird geladen…" : "Keine Daten. Bitte Schritt 4 wiederholen." }}
          </p>
          <v-btn v-if="!assigning" color="green accent-3" class="mt-3 black--text" depressed @click="step = 4">Zurück</v-btn>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="wizard-bottom" v-if="step !== 4 || planningMode !== 'assistant'">
      <div class="wizard-bottom-inner">
        <v-btn v-if="step !== 2" text dark @click="prevStep" :disabled="step === 1 || assigning" class="wizard-nav-btn">
          <v-icon left small>mdi-arrow-left</v-icon> Zurück
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="step === 5" depressed color="error" class="mr-4 wizard-nav-btn" @click="confirmDeleteDialog = true" :loading="deleting">
          <v-icon left small>mdi-delete-sweep</v-icon> Plan leeren
        </v-btn>
        <v-btn v-if="step === 5" depressed @click="$emit('close-dialog')" class="wizard-finish-btn">
          <v-icon left small>mdi-check-circle</v-icon> Fertigstellen & Schliessen
        </v-btn>
        <v-btn
          v-else-if="step !== 2"
          depressed
          @click="handleWizardNext"
          :disabled="step === 4 || (step === 3 && !employeeSettingsCanProceed)"
          class="wizard-next-btn">
          Weiter <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="showConfirmationDialog" max-width="480px" persistent>
      <v-card dark class="rounded-xl">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="amber">mdi-alert-circle-outline</v-icon>
          <span style="font-size: 1rem">Nicht alle MA bereit</span>
        </v-card-title>
        <v-card-text class="text-body-2">
          <span v-if="confirmationDialogData.notReadyNames.length > 0">
            Folgende MA werden <strong>ignoriert</strong>:
            <div class="mt-2" style="max-height: 200px; overflow: auto">
              <v-chip v-for="name in confirmationDialogData.notReadyNames" :key="name" small outlined class="mr-1 mb-1" color="amber">{{
                name
              }}</v-chip>
            </div>
          </span>
          <div class="mt-3">
            Fortfahren mit <strong>{{ confirmationDialogData.readyEmployeeIds.length }}</strong> Mitarbeitern?
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showConfirmationDialog = false">Abbrechen</v-btn>
          <v-btn color="amber" text @click="proceedToAssignment" class="font-weight-bold">Fortfahren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="480px" dark>
      <v-card dark class="rounded-xl">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="red">mdi-delete-sweep</v-icon>
          <span style="font-size: 1rem">Plan komplett leeren?</span>
        </v-card-title>
        <v-card-text class="text-body-2">
          Möchtest du wirklich alle Schichten im geplanten Zeitraum löschen? Das kann nicht rückgängig gemacht werden.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDeleteDialog = false" :disabled="deleting">Abbrechen</v-btn>
          <v-btn color="red" text @click="executeDelete" :loading="deleting" class="font-weight-bold">Ja, leeren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { resolvePlanningMonth } from "./planningStandards"
import api from "@/api"
import WeeklyTemplatePlanner from "./WeeklyTemplatePlanner.vue"
import ShiftManagementEmployeeSettings from "./shiftManagementMainDialogCreateEmployeeModern.vue"
import ShiftManagementRosterView from "./shiftManagementRosterView.vue"
import ShiftManagementMainDialogCreateSpecialDays from "./shiftManagementMainDialogCreateSpecialDays.vue"
import ShiftManagementAssistant from "./shiftManagementAssistant.vue"
import { mapState } from "vuex"

export default {
  name: "ArbeitsplanStepper",
  components: {
    WeeklyTemplatePlanner,
    ShiftManagementEmployeeSettings,
    ShiftManagementRosterView,
    ShiftManagementMainDialogCreateSpecialDays,
    ShiftManagementAssistant,
  },
  props: {
    preSelectedStudio: { type: String, default: null },
    existingPlans: { type: Array, default: () => [] },
    existingPlansStudioId: { type: String, default: null },
  },
  data() {
    return {
      availableEmployeesToAdd: [],
      step: 1,
      loading: true,
      assigning: false,
      assignmentFinished: false,
      planningMonth: this.$moment().format("YYYY-MM"),
      initialLoadComplete: false,
      dateMenu: false,
      selectedStudio: null,
      availableStudios: [],
      settings: { shifts: [] },
      loadedWeeklyTemplate: [],
      employeesBasic: [],
      assignedPlanData: [],
      assignedEmployees: [],
      unassignedShifts: [],
      workHoursSummaries: {},
      workHoursSummaryWarning: "",
      workHoursSummaryRetrying: false,
      workHoursSummaryTimeoutMs: 12000,
      manuallyAddedEmployeeIds: [],
      publicHolidays: [],
      previousContext: null,
      showConfirmationDialog: false,
      confirmationDialogData: { notReadyNames: [], readyEmployeeIds: [] },
      confirmedEmployeeSettings: [],
      confirmDeleteDialog: false,
      deleting: false,
      planningMode: null,
      employeeSettingsCanProceed: false,
      existingPlansByStudio: {},
      existingPlanCheckLoading: false,
      existingPlanCheckError: null,
      existingPlanCheckRequest: 0,
      existingPlanCheckPromise: null,
      existingPlanCheckStudioId: null,

      stepConfig: [{ label: "Vorlage" }, { label: "Spezialtage" }, { label: "Mitarbeiter" }, { label: "Zuweisung" }, { label: "Einsatzplan" }],
    }
  },
  async created() {
    this.loading = true
    try {
      this.syncProvidedExistingPlans()
      this.availableStudios = await api.fetchAllowedAdminStudios()
      if (this.availableStudios.length > 0) {
        if (this.preSelectedStudio) {
          this.selectedStudio = this.preSelectedStudio
        } else if (!this.selectedStudio) {
          this.selectedStudio = this.availableStudios[0]._id
        }

        // Load everything specifically for the selected studio
        ;[this.settings] = await Promise.all([
          api.shiftManagement.fetchSettings(this.selectedStudio),
          this.fetchWeeklyTemplate(),
          this.fetchBasicEmployees(),
          this.fetchHolidaysAndContext(),
        ])
      }
    } catch (error) {
      this.$toast.error("Fehler beim Initialisieren.")
      console.error("Init Error:", error)
    } finally {
      this.loading = false
      this.initialLoadComplete = true
    }
  },
  watch: {
    existingPlans() {
      this.syncProvidedExistingPlans()
    },
    existingPlansStudioId() {
      this.syncProvidedExistingPlans()
    },
    selectedStudio(newStudio, oldStudio) {
      if (newStudio !== oldStudio) {
        this.existingPlanCheckRequest += 1
        this.existingPlanCheckLoading = false
        this.existingPlanCheckError = null
        this.existingPlanCheckPromise = null
        this.existingPlanCheckStudioId = null
      }
    },
    step(newStep, oldStep) {
      if (this.initialLoadComplete && newStep === 1 && oldStep > 1) this.fetchWeeklyTemplate()
      if (newStep === 2) this.ensureExistingPlansLoaded()
      if (newStep === 3) {
        this.employeeSettingsCanProceed = false
        if (this.loadedWeeklyTemplate.length === 0 && !this.loadingTemplate) this.fetchWeeklyTemplate()
      }
      if (newStep === 4) {
        this.assignmentFinished = false
        this.planningMode = null
        this.startAssistantPlanner()
      }
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    planningDateRange() {
      const start = this.$moment(this.planningMonth, "YYYY-MM").startOf("month").format("YYYY-MM-DD")
      const end = this.$moment(this.planningMonth, "YYYY-MM").endOf("month").format("YYYY-MM-DD")
      return [start, end]
    },
    monthDisplayText() {
      return this.$moment(this.planningMonth, "YYYY-MM").format("MMMM YYYY")
    },
    hasExistingPlanOverview() {
      if (!this.selectedStudio) return false
      return Object.prototype.hasOwnProperty.call(this.existingPlansByStudio, String(this.selectedStudio))
    },
    selectedExistingPlan() {
      if (!this.hasExistingPlanOverview) return null
      const plans = this.existingPlansByStudio[String(this.selectedStudio)] || []
      return plans.find((plan) => String(plan && plan._id) === String(this.planningMonth)) || null
    },
    readyConfirmedEmployees() {
      return (this.confirmedEmployeeSettings || []).filter((employee) => employee?.planningSettings?.isReadyForPlanning === true)
    },
    hasCompleteConfirmedWorkHoursSummaries() {
      return (
        this.readyConfirmedEmployees.length > 0 &&
        this.readyConfirmedEmployees.every(
          (employee) => employee.workHoursSummaryUnavailable !== true && this.isValidWorkHoursSummary(employee.hoursSummary),
        )
      )
    },
  },
  methods: {
    withTimeout(promise, timeoutMs, message) {
      let timeoutId
      const timeout = new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => reject(new Error(message || "Zeitüberschreitung")), timeoutMs)
      })
      return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId))
    },
    isValidWorkHoursSummary(summary) {
      const total = summary && summary.total
      if (!total) return false
      const hasNumber = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
      return (
        hasNumber(summary.contractPercentage) &&
        hasNumber(summary.targetWorkdays) &&
        hasNumber(total.grossTargetHours) &&
        hasNumber(total.netTargetHours) &&
        hasNumber(total.plannedHours)
      )
    },
    syncProvidedExistingPlans() {
      if (!this.existingPlansStudioId || !Array.isArray(this.existingPlans)) return
      this.$set(this.existingPlansByStudio, String(this.existingPlansStudioId), this.existingPlans)
    },
    async ensureExistingPlansLoaded(force = false) {
      if (!this.selectedStudio) return false
      const studioId = String(this.selectedStudio)
      if (!force && Object.prototype.hasOwnProperty.call(this.existingPlansByStudio, studioId)) {
        this.existingPlanCheckError = null
        return true
      }
      if (!force && this.existingPlanCheckLoading && this.existingPlanCheckStudioId === studioId && this.existingPlanCheckPromise) {
        try {
          await this.existingPlanCheckPromise
          return String(this.selectedStudio) === studioId && Object.prototype.hasOwnProperty.call(this.existingPlansByStudio, studioId)
        } catch (error) {
          return false
        }
      }

      const requestId = ++this.existingPlanCheckRequest
      this.existingPlanCheckLoading = true
      this.existingPlanCheckError = null
      this.existingPlanCheckStudioId = studioId
      try {
        const request = api.shiftManagement.fetchPlansOverview(studioId)
        this.existingPlanCheckPromise = request
        const plans = await request
        if (!Array.isArray(plans)) throw new Error("Ungültige Monatsübersicht")
        this.$set(this.existingPlansByStudio, studioId, plans)
        if (requestId !== this.existingPlanCheckRequest || String(this.selectedStudio) !== studioId) return false
        return true
      } catch (error) {
        console.error("Existing Plan Check Error:", error)
        if (requestId === this.existingPlanCheckRequest && String(this.selectedStudio) === studioId) {
          this.existingPlanCheckError = error
        }
        return false
      } finally {
        if (requestId === this.existingPlanCheckRequest) {
          this.existingPlanCheckLoading = false
          this.existingPlanCheckPromise = null
          this.existingPlanCheckStudioId = null
        }
      }
    },
    async handleSpecialDaysNext() {
      const studioId = String(this.selectedStudio || "")
      const monthId = String(this.planningMonth || "")
      const checked = await this.ensureExistingPlansLoaded()
      if (String(this.selectedStudio || "") !== studioId || String(this.planningMonth || "") !== monthId) return
      if (!checked || this.selectedExistingPlan) {
        if (this.selectedExistingPlan) this.$toast.info(`${this.monthDisplayText} wird direkt über die Monatsansicht bearbeitet.`)
        return
      }
      this.step = 3
    },
    openExistingMonth() {
      if (!this.selectedExistingPlan || !this.selectedStudio) return
      this.$emit("open-existing-month", {
        studioId: String(this.selectedStudio),
        monthId: String(this.planningMonth),
      })
    },
    handleWizardNext() {
      if (this.step === 3 && this.$refs.employeeSettings) {
        this.$refs.employeeSettings.triggerAssignment()
        return
      }
      this.nextStep()
    },
    onAddEmployee(userId) {
      if (!this.manuallyAddedEmployeeIds.includes(userId)) {
        this.manuallyAddedEmployeeIds.push(userId)
        // Wir übergeben false für switchStep, damit er im selben Screen bleibt und nur neu rendert
        void this.handleAssignmentComplete(this.assignedPlanData, false)
      }
    },
    async onMonthChanged(newMonth) {
      this.planningMonth = newMonth
      this.workHoursSummaries = {}
      this.dateMenu = false
      const studioId = String(this.selectedStudio || "")
      const monthId = String(this.planningMonth || "")
      const checked = await this.ensureExistingPlansLoaded()
      if (String(this.selectedStudio || "") !== studioId || String(this.planningMonth || "") !== monthId) return
      if (checked && !this.selectedExistingPlan) await this.fetchHolidaysAndContext()
    },
    async refreshData() {
      if (!this.selectedStudio) return
      this.assigning = true
      try {
        const response = await api.shiftManagement.fetchPlan(this.selectedStudio, this.planningDateRange[0], this.planningDateRange[1], true)
        const responseData = response && response.data ? response.data : response
        const freshShifts = responseData && responseData.shifts ? responseData.shifts : Array.isArray(responseData) ? responseData : []
        if (!freshShifts) throw new Error("Keine Schicht-Daten empfangen")
        await this.handleAssignmentComplete(freshShifts, false)
      } catch (e) {
        console.error("Refresh Error:", e)
        this.$toast.error("Konnte Ansicht nicht aktualisieren.")
      } finally {
        this.assigning = false
      }
    },
    async fetchBasicEmployees() {
      if (!this.selectedStudio) return
      try {
        const emps = await api.shiftManagement.fetchShiftManagementEmployee(this.selectedStudio)
        this.employeesBasic = emps.filter((e) => {
          const studioInfo = e.studio?.find((s) => String(s._id) === String(this.selectedStudio))
          if (!studioInfo) return false
          return studioInfo.hide !== true || studioInfo.showInCalendarAnyway === true
        })
      } catch (e) {
        console.error("Employees Error", e)
      }
    },
    async fetchCurrentWorkHoursSummaries() {
      if (!this.selectedStudio || !this.planningDateRange || this.planningDateRange.length !== 2) {
        throw new Error("Studio oder Planungszeitraum fehlt.")
      }

      const studioId = String(this.selectedStudio)
      const [startDate, endDate] = this.planningDateRange
      const response = await this.withTimeout(
        api.shiftManagement.fetchWorkHoursSummaries([studioId], startDate, endDate),
        this.workHoursSummaryTimeoutMs,
        "Die Sollstunden-Berechnung hat zu lange gedauert.",
      )
      if (!response || !response.summaries || typeof response.summaries !== "object") {
        throw new Error("Zentrale Stundenübersicht fehlt.")
      }

      const currentRange = this.planningDateRange
      if (String(this.selectedStudio) !== studioId || currentRange[0] !== startDate || currentRange[1] !== endDate) {
        throw new Error("Studio oder Planungszeitraum wurde während des Ladens geändert.")
      }

      this.workHoursSummaries = response.summaries
      return response.summaries
    },
    getEmployeeUserId(employee) {
      return String(employee?.user?._id || employee?.user || "")
    },
    enrichEmployeeWithWorkHours(employee, summaries) {
      const userId = this.getEmployeeUserId(employee)
      const summary = summaries && summaries[userId]
      const sollStunden = summary?.total?.netTargetHours
      const plannedHours = summary?.total?.plannedHours

      if (!userId || !this.isValidWorkHoursSummary(summary)) {
        const fallbackSoll = Number(employee?.sollStunden)
        const fallbackPlanned = Number(employee?.plannedHours)
        return {
          ...employee,
          sollStunden: Number.isFinite(fallbackSoll) ? fallbackSoll : 0,
          plannedHours: Number.isFinite(fallbackPlanned) ? fallbackPlanned : 0,
          hoursSummary: employee?.hoursSummary || null,
          workHoursSummaryUnavailable: true,
        }
      }

      return {
        ...employee,
        sollStunden: Number(sollStunden),
        plannedHours: Number(plannedHours),
        hoursSummary: summary,
        workHoursSummaryUnavailable: false,
      }
    },
    enrichEmployeesWithWorkHours(employees, summaries) {
      return (employees || []).map((employee) => this.enrichEmployeeWithWorkHours(employee, summaries))
    },
    getEffectiveMonthSettings(employee) {
      return resolvePlanningMonth(employee?.shiftPlanningSettings?.[this.selectedStudio] || {}, this.planningMonth)
    },
    getSavedPlanningSettings(employee) {
      const currentSettings = employee?.planningSettings || {}
      if (Object.keys(currentSettings).length > 0) return currentSettings

      const backendSettings = this.getEffectiveMonthSettings(employee)
      if (Object.keys(backendSettings).length > 0) return backendSettings

      try {
        const storageKey = `planningSettings_${employee?._id}_${this.selectedStudio}`
        const localSettings = JSON.parse(localStorage.getItem(storageKey)) || {}
        return {
          ...localSettings,
          // Der Legacy-Key hat keinen Monat und ist daher keine erlaubte
          // Quelle für ein Planungsprozent.
          percentages: {},
        }
      } catch (error) {
        console.warn("Ungültige lokale Planungseinstellungen werden ignoriert:", error)
        return {}
      }
    },
    async persistConfirmedPlanningSettings() {
      const employees = this.confirmedEmployeeSettings || []
      const results = await Promise.all(
        employees.map(async (employee) => {
          if (!employee?._id || !employee.planningSettings) return false
          const monthlySettings = {
            ...employee.planningSettings,
            isApprentice: employee.isApprentice === true,
            schoolDays: Array.isArray(employee.schoolDays) ? employee.schoolDays : [],
          }
          await api.shiftManagement.saveMonthlyPlanningSettings(employee._id, this.selectedStudio, this.planningMonth, monthlySettings)
          return true
        }),
      )
      if (results.some((saved) => saved !== true)) throw new Error("Nicht alle Monatseinstellungen konnten gespeichert werden.")
    },
    async handleStudioChange(newStudioId) {
      if (!newStudioId || !this.initialLoadComplete) return
      this.loading = true
      this.workHoursSummaries = {}
      try {
        this.settings = await api.shiftManagement.fetchSettings(newStudioId)
        await this.fetchWeeklyTemplate()
        await this.fetchBasicEmployees()
        await this.fetchHolidaysAndContext()
      } catch (error) {
        console.error("Error changing studio settings:", error)
      } finally {
        this.loading = false
      }
    },
    async fetchWeeklyTemplate() {
      if (!this.selectedStudio) {
        this.loadedWeeklyTemplate = []
        return
      }
      this.loading = true
      try {
        this.loadedWeeklyTemplate = await api.shiftManagement.fetchWeeklyTemplate(this.selectedStudio)
      } catch (error) {
        this.$toast.error("Vorlage-Ladefehler.")
        this.loadedWeeklyTemplate = []
      } finally {
        this.loading = false
      }
    },
    handleTemplateUpdate(savedTemplate) {
      this.loadedWeeklyTemplate = savedTemplate
      this.$toast.success("Vorlage aktualisiert.")
    },
    async fetchHolidaysAndContext() {
      if (!this.selectedStudio || !this.planningDateRange || this.planningDateRange.length < 2) return
      try {
        const [holidays, context] = await Promise.all([
          api.shiftManagement.fetchPublicHolidays(this.selectedStudio, this.planningDateRange[0], this.planningDateRange[1]),
          api.shiftManagement.fetchPreviousMonthContext(this.selectedStudio, this.planningDateRange[0]),
        ])
        this.publicHolidays = holidays || []
        this.previousContext = context || null
      } catch (e) {
        console.error("Holidays Error:", e)
      }
    },
    async triggerAssignment() {
      if (!this.confirmationDialogData.readyEmployeeIds || this.confirmationDialogData.readyEmployeeIds.length === 0) {
        this.$toast.error("Keine Mitarbeiter ausgewählt.")
        return
      }
      if (!this.confirmedEmployeeSettings || this.confirmedEmployeeSettings.length === 0) {
        this.$toast.error("Mitarbeiterdaten nicht gefunden.")
        return
      }
      if (!this.hasCompleteConfirmedWorkHoursSummaries) {
        this.workHoursSummaryWarning = "Für die automatische Zuweisung fehlen verlässliche Sollstunden. Bitte berechne die Stunden erneut."
        this.$toast.warning("Automatische Zuweisung bleibt bis zur Sollstunden-Berechnung gesperrt.")
        return
      }
      this.assigning = true
      this.assignmentFinished = false
      const planningSettingsPayload = {}
      const [startDate, endDate] = this.planningDateRange
      try {
        await this.persistConfirmedPlanningSettings()
        const readyEmployeeIdSet = new Set(this.confirmationDialogData.readyEmployeeIds.map((id) => String(id)))
        const readyEmployeesData = this.confirmedEmployeeSettings.filter((emp) => readyEmployeeIdSet.has(this.getEmployeeUserId(emp)))
        readyEmployeesData.forEach((employee) => {
          const savedSettings = this.getSavedPlanningSettings(employee)
          const existing = employee.planningSettings || {}
          planningSettingsPayload[employee._id.toString()] = {
            percentages: savedSettings.percentages || {},
            recurringAbsence: savedSettings.recurringAbsence || [],
            ignore: savedSettings.hasOwnProperty("isReadyForPlanning") ? !savedSettings.isReadyForPlanning : existing.ignore || false,
            fixedShifts: savedSettings.fixedShifts || existing.fixedShifts || {},
            longCommute: savedSettings.longCommute || existing.longCommute || false,
          }
        })
        await api.shiftManagement.generatePlanFromTemplate(this.selectedStudio, startDate, endDate)
        const updatedPlan = await api.shiftManagement.assignEmployeesToPlan(
          this.selectedStudio,
          startDate,
          endDate,
          planningSettingsPayload,
          this.confirmationDialogData.readyEmployeeIds,
        )
        if (!updatedPlan || updatedPlan.length === 0) throw new Error("Leerer Plan.")
        await this.handleAssignmentComplete(updatedPlan, false)
        this.assignmentFinished = true
      } catch (error) {
        this.$toast.error("Zuweisungsfehler.")
        console.error("Assignment Error:", error)
      } finally {
        this.assigning = false
      }
    },
    async handleAssignmentComplete(updatedPlan, switchStep = true) {
      this.assignedPlanData = updatedPlan.filter((p) => p.assignedEmployee)
      this.unassignedShifts = updatedPlan.filter((p) => !p.assignedEmployee)
      let preparationSucceeded = false
      if (switchStep) this.assigning = true
      try {
        const allStudioEmployees = await api.shiftManagement.fetchShiftManagementEmployee(this.selectedStudio)
        let workHoursSummaries = {}
        try {
          workHoursSummaries = await this.fetchCurrentWorkHoursSummaries()
          this.workHoursSummaryWarning = ""
        } catch (summaryError) {
          console.warn("Roster wird ohne aktuelle Stunden-Summary vorbereitet:", summaryError)
          this.workHoursSummaryWarning =
            "Die zentrale Stundenübersicht ist vorübergehend nicht verfügbar. Der Einsatzplan bleibt vollständig nutzbar."
          this.$toast.info("Plan geladen; einzelne Stundenwerte sind vorübergehend nicht verfügbar.")
        }
        let sourceEmployees = this.confirmedEmployeeSettings
        if (!sourceEmployees || sourceEmployees.length === 0) {
          sourceEmployees = allStudioEmployees.filter((emp) => emp.studio && emp.studio.some((s) => String(s._id) === String(this.selectedStudio)))
        }
        const employeeMap = new Map(
          sourceEmployees.map((e) => {
            const uId = String(e.user._id || e.user)
            return [uId, e]
          }),
        )
        let assignedUserIds = [...new Set(this.assignedPlanData.map((p) => String(p.assignedEmployee._id || p.assignedEmployee)))]

        sourceEmployees.forEach((e) => {
          const uId = String(e.user._id || e.user)
          const savedSettings = this.getSavedPlanningSettings(e)

          // Überprüfung: Auf "Bereit" gesetzt ODER manuell im Nachhinein hinzugefügt
          if ((savedSettings.isReadyForPlanning || this.manuallyAddedEmployeeIds.includes(uId)) && !assignedUserIds.includes(uId)) {
            assignedUserIds.push(uId)
          }
        })

        this.assignedEmployees = assignedUserIds
          .map((userId) => {
            let employeeData = employeeMap.get(userId)
            if (!employeeData) employeeData = allStudioEmployees.find((e) => String(e.user._id || e.user) === userId)
            if (!employeeData) return null
            const savedSettings = this.getSavedPlanningSettings(employeeData)
            const planningSettings = {
              percentages: savedSettings.percentages || {},
              recurringAbsence: savedSettings.recurringAbsence || [],
              isReadyForPlanning: savedSettings.isReadyForPlanning || false,
            }
            const employeeWithWorkHours = this.enrichEmployeeWithWorkHours(employeeData, workHoursSummaries)
            return {
              ...employeeWithWorkHours,
              _id: userId,
              planningSettings,
            }
          })
          .filter((e) => e !== null)

        if (this.assignedEmployees.some((employee) => employee.workHoursSummaryUnavailable)) {
          this.workHoursSummaryWarning =
            this.workHoursSummaryWarning ||
            "Für einzelne Mitarbeitende fehlt die aktuelle Stundenübersicht. Der Einsatzplan bleibt vollständig nutzbar."
        }

        // Berechnung der noch nicht eingeblendeten Mitarbeiter für das Dropdown am unteren Rand
        this.availableEmployeesToAdd = sourceEmployees
          .filter((e) => !assignedUserIds.includes(String(e.user._id || e.user)))
          .map((e) => ({
            _id: String(e.user._id || e.user),
            fullName: e.fullName,
            picture: e.picture,
          }))
          .sort((a, b) => a.fullName.localeCompare(b.fullName))
        preparationSucceeded = true
      } catch (error) {
        console.error("Roster Prep Error:", error)
        this.$toast.error("Fehler beim Aufbereiten.")
      } finally {
        if (switchStep) {
          this.assigning = false
          if (preparationSucceeded) this.step = 5
        }
      }
    },
    async executeDelete() {
      if (!this.assignedPlanData.length && !this.unassignedShifts.length) {
        this.confirmDeleteDialog = false
        return
      }
      this.deleting = true
      try {
        const allShifts = [...this.assignedPlanData, ...this.unassignedShifts]
        const promises = allShifts.map((s) => api.shiftManagement.deleteShift(s._id || s))
        await Promise.all(promises)
        this.$toast.success("Plan erfolgreich geleert.")
        this.confirmDeleteDialog = false
        this.assignedPlanData = []
        this.unassignedShifts = []
        await this.handleAssignmentComplete([], false)
      } catch (err) {
        console.error("Fehler beim Löschen:", err)
        this.$toast.error("Fehler beim Löschen des Plans.")
      } finally {
        this.deleting = false
      }
    },
    async startAssistantPlanner() {
      this.assigning = true
      this.workHoursSummaryWarning = ""
      try {
        const [startDate, endDate] = this.planningDateRange
        await this.persistConfirmedPlanningSettings()

        // Vor jeder Automatik werden die gerade gespeicherten Monatswerte
        // zentral neu berechnet. Erst danach dürfen Schichten generiert werden.
        const summaries = await this.fetchCurrentWorkHoursSummaries()
        this.employeesBasic = this.enrichEmployeesWithWorkHours(this.employeesBasic, summaries)
        this.confirmedEmployeeSettings = this.enrichEmployeesWithWorkHours(this.confirmedEmployeeSettings, summaries)
        if (!this.hasCompleteConfirmedWorkHoursSummaries) {
          throw new Error("Für mindestens eine aktive Person fehlt die zentrale Stundenübersicht.")
        }

        await api.shiftManagement.generatePlanFromTemplate(this.selectedStudio, startDate, endDate)

        this.planningMode = "assistant"
      } catch (e) {
        console.error("Start Assistant Error:", e)
        this.workHoursSummaryWarning =
          "Die Sollstunden konnten nicht vollständig geprüft werden. Es wurden keine neuen automatischen Zuweisungen gestartet."
        this.$toast.warning("Assistent gesperrt: Sollstunden bitte erneut berechnen.")
      } finally {
        this.assigning = false
      }
    },
    async retryAssistantSummary() {
      if (this.workHoursSummaryRetrying) return
      this.workHoursSummaryRetrying = true
      try {
        await this.startAssistantPlanner()
        if (this.planningMode !== "assistant") throw new Error("Unvollständige Stundenübersicht")
      } catch (error) {
        console.warn("Sollstunden-Retry fehlgeschlagen:", error)
        this.workHoursSummaryWarning =
          "Die Sollstunden sind weiterhin unvollständig. Der Einsatzplan bleibt sichtbar; automatische Vorschläge bleiben gesperrt."
      } finally {
        this.workHoursSummaryRetrying = false
      }
    },
    async handleAssistantFinish() {
      this.assigning = true
      try {
        const response = await api.shiftManagement.fetchPlan(this.selectedStudio, this.planningDateRange[0], this.planningDateRange[1], true)
        const responseData = response && response.data ? response.data : response
        const freshShifts = responseData && responseData.shifts ? responseData.shifts : Array.isArray(responseData) ? responseData : []
        await this.handleAssignmentComplete(freshShifts, true)
      } catch (e) {
        console.error("Assistant Finish Error:", e)
        this.$toast.error("Fehler beim Abschliessen der Planung.")
      } finally {
        this.assigning = false
      }
    },
    async nextStep() {
      if (this.step === 2) {
        await this.handleSpecialDaysNext()
        return
      }
      if (this.step === 1) {
        if (this.$refs.weeklyPlanner) {
          try {
            await this.$refs.weeklyPlanner.saveTemplate()
          } catch (e) {
            console.error("Auto-save failed", e)
          }
        }
      }
      if (this.step === 3) {
        if (this.$refs.employeeSettings) {
          const status = this.$refs.employeeSettings.getConfirmationStatus()
          this.confirmationDialogData.notReadyNames = status.notReadyNames
          this.confirmationDialogData.readyEmployeeIds = status.readyEmployeeIds
          this.confirmedEmployeeSettings = this.$refs.employeeSettings.employees || []
          if (!this.hasCompleteConfirmedWorkHoursSummaries) {
            this.workHoursSummaryWarning = "Für die automatische Zuweisung fehlen verlässliche Sollstunden. Bitte berechne die Stunden erneut."
            this.$toast.warning("Automatische Zuweisung bleibt gesperrt.")
            return
          }
          if (!status.allMarked) {
            this.showConfirmationDialog = true
            return
          }
        }
      }
      if (this.step < 5) this.step++
    },
    prevStep() {
      if (this.step > 1) this.step--
    },
    proceedToAssignment() {
      this.showConfirmationDialog = false
      if (!this.hasCompleteConfirmedWorkHoursSummaries) {
        this.workHoursSummaryWarning = "Für die automatische Zuweisung fehlen verlässliche Sollstunden. Bitte berechne die Stunden erneut."
        this.$toast.warning("Automatische Zuweisung bleibt gesperrt.")
        return
      }
      this.step = 4
    },
    autoSelectStudio() {
      if (this.availableStudios.length === 1 && !this.selectedStudio) {
        this.$nextTick(() => {
          this.selectedStudio = this.availableStudios[0]._id
        })
      }
      return ""
    },
  },
}
</script>

<style scoped>
.wizard-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: #1a1a1a;
  color: white;
}

/* === HEADER === */
.wizard-header {
  flex-shrink: 0;
  padding: 16px 32px;
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.wizard-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 700px;
  margin: 0 auto;
}
.wizard-step {
  display: flex;
  align-items: center;
  flex: 1;
  position: relative;
}
.wizard-step.clickable {
  cursor: pointer;
}
.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
}
.wizard-step.active .step-circle {
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  color: white;
  border-color: #4caf50;
  box-shadow: 0 0 16px rgba(76, 175, 80, 0.4);
}
.wizard-step.completed .step-circle {
  background: #2e7d32;
  color: white;
  border-color: #2e7d32;
}
.step-label {
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 8px;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.3);
  transition: color 0.3s;
}
.wizard-step.active .step-label {
  color: #66bb6a;
}
.wizard-step.completed .step-label {
  color: rgba(255, 255, 255, 0.5);
}
.step-line {
  flex: 1;
  height: 2px;
  margin: 0 12px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 1px;
  transition: background 0.5s;
}
.step-line.filled {
  background: #2e7d32;
}

/* === BODY === */
.wizard-body {
  flex: 1;
  overflow: auto;
  padding: 24px 32px 100px;
}
.wizard-body-fullheight {
  padding: 0;
}
.wizard-step-content {
  width: 100%;
  animation: fadeSlideIn 0.3s ease;
}
.wizard-step-fullheight {
  height: 100%;
  display: flex;
  flex-direction: column;
}
@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.wizard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
}
.wizard-select {
  border-radius: 10px !important;
}
.wizard-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.06) !important;
  border-radius: 10px !important;
}
.wizard-month-btn {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 0 16px !important;
}

.wizard-status-card {
  min-height: 190px;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 28px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.035);
}
.wizard-status-card-existing {
  border-color: rgba(255, 193, 7, 0.3);
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 255, 255, 0.025));
}
.wizard-status-card-error {
  border-color: rgba(239, 83, 80, 0.3);
  background: linear-gradient(135deg, rgba(239, 83, 80, 0.09), rgba(255, 255, 255, 0.025));
}
.wizard-status-icon {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: rgba(255, 193, 7, 0.13);
}
.wizard-status-icon.error-icon {
  background: rgba(239, 83, 80, 0.13);
}
.wizard-status-copy {
  max-width: 720px;
  color: rgba(255, 255, 255, 0.58);
  line-height: 1.6;
}

/* === BOTTOM BAR === */
.wizard-bottom {
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 12px 32px;
}
.wizard-bottom-inner {
  display: flex;
  align-items: center;
  max-width: 100%;
}
.wizard-nav-btn {
  text-transform: none !important;
  letter-spacing: 0.3px !important;
}
.wizard-next-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 0 24px !important;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3) !important;
}
.wizard-finish-btn {
  background: linear-gradient(135deg, #2196f3, #1565c0) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 0 24px !important;
  box-shadow: 0 4px 16px rgba(33, 150, 243, 0.3) !important;
}
</style>
